import { useState } from "react";
import { Modal, Form } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useDispatch } from "react-redux";
import { loginUser, socialSignup, resendOtp } from "../../redux/actions/auth"; // adjust path
import { toast } from "react-toastify";
import SocialLoginActions from "./SocialLoginActions";
import EnterOtp from "./EnterOtp";

// Validation schema
const schema = yup.object({
    email: yup
        .string()
        .email("Enter a valid email")
        .required("Email is required"),
    password: yup
        .string()
        .required("Password is required")
        .min(6, "Minimum 6 characters"),
    // terms: yup.boolean().oneOf([true], "You must accept the terms"),
});

const Signin = ({ show, onHide, openSignup, openForgotPassword }) => {
    const dispatch = useDispatch();
    const [showPassword, setShowPassword] = useState(false);
    const [showOtpModal, setShowOtpModal] = useState(false);
    const [userEmail, setUserEmail] = useState("");
    const [userId, setUserId] = useState("");

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(schema),
        mode: "onBlur",
        defaultValues: {
            email: "",
            password: "",
            terms: false,
        },
    });

    // Social login handler
    const handleSocialLogin = async (provider) => {
        try {
            const res = await dispatch(socialSignup({ provider }));
            if (res.success) {
                toast.success(`${provider.charAt(0).toUpperCase() + provider.slice(1)} login successful!`);
                onHide(); // Close Signin modal
            } else {
                toast.error(res.message || "Social login failed");
            }
        } catch (err) {
            toast.error(err.message || "Something went wrong");
        }
    };


    // Login submit
    const onSubmit = async (data) => {
        console.log("Signin form values:", data);
        try {
            const payload = {
                email: data.email,
                password: data.password,
            };

            const response = await dispatch(loginUser(payload)).unwrap();

            // Check if OTP verification is needed
            if (response?.data?.is_otp_verified === 0) {
                setUserEmail(data.email);
                setUserId(response?.data?._id);
                try {
                    await dispatch(resendOtp({ id: response?.data?._id })).unwrap();
                    onHide();
                    setShowOtpModal(true);
                    toast.success("OTP sent to your email. Please verify your account.");
                } catch (error) {
                    toast.error("Failed to send OTP. Please try again.");
                }
            } else {
                // toast.success(response?.message || "Logged in successfully!");
                onHide();
            }
        } catch (error) {
            console.error("Login Error:", error);
            toast.error(error?.message || "Invalid email or password");
        }
    };

    const handleOtpSuccess = () => {
        setShowOtpModal(false);
        toast.success("Account verified successfully!");
    };

    return (
        <>
            <Modal show={show} onHide={onHide} centered>
                <Modal.Header closeButton className="border-0" />
                <Modal.Body className="pt-0">
                    <div className="commn-modal-signin">
                        <h2>Sign In</h2>
                        <p>
                            It was popularised in the 1960s with the release of Letraset sheets
                            containing Lorem Ipsum.
                        </p>

                        {/* Social Signin */}
                        <SocialLoginActions />

                        <Form onSubmit={handleSubmit(onSubmit)}>
                            {/* Email */}
                            <Form.Group className="mb-3" controlId="signinEmail">
                                <Form.Control type="email" placeholder="Email" {...register("email")} />
                                {errors.email && (
                                    <p className="text-danger" style={{ textAlign: "left", marginTop: "5px" }}>
                                        {errors.email.message}
                                    </p>
                                )}
                            </Form.Group>

                            {/* Password with Eye Toggle */}
                            <Form.Group className="mb-1" controlId="signinPassword" style={{ position: "relative" }}>
                                <Form.Control
                                    type={showPassword ? "text" : "password"}
                                    placeholder="Password"
                                    style={{
                                        background: "#F5F9FF",
                                        borderRadius: "12px",
                                        paddingRight: "45px",
                                        height: "50px",
                                    }}
                                    {...register("password")}
                                />
                                <span
                                    onClick={() => setShowPassword(!showPassword)}
                                    style={{
                                        position: "absolute",
                                        right: "15px",
                                        top: "25px",
                                        transform: "translateY(-50%)",
                                        cursor: "pointer",
                                    }}
                                >
                                    {showPassword ? (
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <path
                                                d="M12 5C7 5 2.73 8.11 1 12C2.73 15.89 7 19 12 19C17 19 21.27 15.89 23 12C21.27 8.11 17 5 12 5Z"
                                                stroke="#2C3E50"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                            <circle cx="12" cy="12" r="3" stroke="#2C3E50" strokeWidth="2" />
                                        </svg>
                                    ) : (
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <path
                                                d="M17.94 17.94C16.23 19.24 14.21 20 12 20C7 20 2.73 16.89 1 13C1.63 11.61 2.62 10.36 3.88 9.32M9.9 4.24C10.58 4.08 11.28 4 12 4C17 4 21.27 7.11 23 11C22.37 12.39 21.38 13.64 20.12 14.68"
                                                stroke="#2C3E50"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                            <line
                                                x1="2"
                                                y1="2"
                                                x2="22"
                                                y2="22"
                                                stroke="#2C3E50"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                            />
                                        </svg>
                                    )}
                                </span>
                            </Form.Group>

                            {/* Error and Forgot Password on same line */}
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", minHeight: "25px", marginBottom: "1rem", marginTop: "5px" }}>
                                <div>
                                    {errors.password && (
                                        <p className="text-danger" style={{ margin: 0, fontSize: "14px" }}>
                                            {errors.password.message}
                                        </p>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    className="link-like"
                                    onClick={openForgotPassword}
                                    style={{ background: "none", border: "none", color: "grey", cursor: "pointer", padding: 0 }}
                                >
                                    Forgot Password?
                                </button>
                            </div>


                            {/* Terms */}
                            {/* <Form.Group className="mb-3" controlId="signinTerms">
                            <Form.Check
                                type="checkbox"
                                label="I agree to The Terms of Service and Privacy Policy"
                                {...register("terms")}
                            />
                            {errors.terms && (
                                <p className="text-danger" style={{ textAlign: "left", marginTop: "5px" }}>
                                    {errors.terms.message}
                                </p>
                            )}
                        </Form.Group> */}

                            {/* Login Button */}
                            <div className="d-grid gap-2">
                                <button type="submit" className="btn btn-primary slide-text-btn">
                                    Log In
                                </button>
                            </div>

                            {/* Create Account */}
                            <div className="bottom-sign-txt mt-3">
                                Don't have an account?{" "}
                                <button
                                    type="button"
                                    className="link-like"
                                    onClick={() => {
                                        onHide();
                                        openSignup();
                                    }}
                                    style={{ background: "none", border: "none", color: "#000", cursor: "pointer", padding: 0 }}
                                >
                                    Sign Up
                                </button>
                            </div>
                        </Form>
                    </div>
                </Modal.Body>
            </Modal>

            <EnterOtp
                show={showOtpModal}
                onHide={() => setShowOtpModal(false)}
                openResetPassword={handleOtpSuccess}
                email={userEmail}
                userId={userId}
            />
        </>
    );
};

export default Signin;
